var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useState, useMemo, useEffect } from 'react';
import { translate } from '@utils/i18n';
import { saveCacheToLocalStorage, loadCacheFromLocalStorage } from '../../../../helpers/localStorage';
// Default filters
const DEFAULT_FILTERS = {
    search: '',
    allowedAttachableTypes: []
};
const useFilteredComponentTypes = ({ componentTypes }) => {
    // Create cache key for component types settings page
    const CACHE_KEY = 'kendo/component_types/settings/index';
    // Load initial filters from localStorage
    const getInitialFilters = () => {
        const cache = loadCacheFromLocalStorage(CACHE_KEY);
        if (cache && cache.filters) {
            return cache.filters;
        }
        return DEFAULT_FILTERS;
    };
    // Filters state with initial value from localStorage
    const [filters, setFilters] = useState(getInitialFilters);
    // Save filters to localStorage whenever they change
    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars, unused-imports/no-unused-vars
        const { search } = filters, filtersToSave = __rest(filters, ["search"]);
        saveCacheToLocalStorage(CACHE_KEY, { filters: filtersToSave });
    }, [filters, CACHE_KEY]);
    // Reset cache functionality
    const onResetCache = () => {
        // eslint-disable-next-line no-alert
        if (!window.confirm(translate('are_you_sure', { namespace: 'generic' })))
            return;
        localStorage.removeItem(CACHE_KEY);
        window.location.reload();
    };
    // Filter component types based on current filters
    const filteredComponentTypes = useMemo(() => {
        if (!componentTypes.length)
            return [];
        return componentTypes.filter((componentType) => {
            // Search filter (searches typeName and description)
            if (filters.search) {
                const searchTerm = filters.search.toLowerCase();
                const matchesTypeName = componentType.typeName.toLowerCase().includes(searchTerm);
                const matchesDescription = componentType.description && componentType.description.toLowerCase().includes(searchTerm);
                if (!matchesTypeName && !matchesDescription) {
                    return false;
                }
            }
            // Allowed Attachable Types filter
            if (filters.allowedAttachableTypes.length > 0) {
                // Blank allowedAttachableTypes means not configured — no types match any filter
                if (!componentType.allowedAttachableTypes || componentType.allowedAttachableTypes.length === 0) {
                    return false;
                }
                const hasMatchingType = componentType.allowedAttachableTypes.some((attachableType) => filters.allowedAttachableTypes.includes(attachableType));
                if (!hasMatchingType) {
                    return false;
                }
            }
            return true;
        });
    }, [componentTypes, filters]);
    return {
        filters,
        setFilters,
        filteredComponentTypes,
        onResetCache,
        CACHE_KEY
    };
};
export default useFilteredComponentTypes;
